import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { PurchasesTracesService } from 'src/app/services/purchases-traces.service';
import { ScholasticYear } from 'src/app/models/scholastic-year.model';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, Subscription } from 'rxjs';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-purchases-traces-statistics',
  templateUrl: './purchases-traces-statistics.component.html',
})
export class PurchasesTracesStatisticsComponent implements OnInit {
  private _baseComponentService = inject(BaseComponentService);
  protected graficoLineeDivisioneCategorie?: Chart;
  protected graficoLineeDivisioneMesi?: Chart;
  protected graficoTortaPercentuali?: Chart;
  protected graficoTortaQuantita?: Chart;

  protected loading: boolean = true;
  protected year: number = new Date().getFullYear();
  private _purchasesTracesService: PurchasesTracesService = inject(
    PurchasesTracesService
  );

  protected _subscription?: Subscription;
  protected schoolYears$ = this._purchasesTracesService.schoolYears$;
  protected categoriesOptions$ =
    this._purchasesTracesService.categoriesOptions$;

  protected filterForm: FormGroup = new FormGroup({
    type: new FormControl('year'),
    year: new FormControl(new Date().getFullYear()),
    school_year: new FormControl(ScholasticYear.getCurrent()),
    category: new FormControl(null),
  });

  ngOnInit(): void {
    this._getData();
    this._subscription = this.filterForm.valueChanges
      .pipe(debounceTime(800))
      .subscribe(() => this._getData());
  }

  protected setYear(value: number): void {
    this.year += value;
    this._getData();
  }

  private get _getFilterObj(): {
    year?: string;
    category?: string;
  } {
    let type: 'year' | 'school_year' = 'year';
    switch (this.filterForm.get('type')?.value) {
      case 'year':
        type = 'year';
        break;
      case 'school_year':
        type = 'school_year';
        break;
    }
    return Object.fromEntries(
      Object.entries({
        year: type === 'year' ? this.filterForm.get('year')?.value : undefined,
        school_year:
          type === 'school_year'
            ? this.filterForm.get('school_year')?.value
            : undefined,
        category:
          this.filterForm.get('category')?.value !== null
            ? this.filterForm.get('category')?.value.toUpperCase().trim()
            : undefined,
      }).filter(([_, v]) => v !== undefined)
    );
  }

  private async _getData() {
    this.loading = true;
    this.graficoLineeDivisioneMesi?.destroy();
    this.graficoLineeDivisioneCategorie?.destroy();
    this._purchasesTracesService
      .getPurchasesTracesStatistics(this._getFilterObj)
      .subscribe({
        next: (response) => {
          this.loading = false;
          setTimeout(() => {
            const monthsLabels = (response?.data?.at(0)?.months || []).map(
              (m) => m.name
            );
            // LINES
            const previousYearData = response.data?.at(0);
            const currentYearData = response.data?.at(1);
            this.graficoLineeDivisioneMesi = new Chart(
              'graficoLineeDivisioneMesi',
              {
                type: 'line',
                data: {
                  labels: monthsLabels,
                  datasets: [
                    {
                      label: `${previousYearData?.year || '-'}`,
                      data: (previousYearData?.months || []).map(
                        (m) => m.totalAmount || 0
                      ),
                      borderColor: '#FF3B30FF',
                      backgroundColor: '#FF3B30FF',
                      fill: false,
                      tension: 0.2,
                      order: 1,
                    },
                    {
                      label: `${currentYearData?.year || '-'} `,
                      data: (currentYearData?.months || []).map(
                        (m) => m.totalAmount || 0
                      ),
                      borderColor: '#32ADE6FF',
                      backgroundColor: '#32ADE6FF',
                      fill: false,
                      tension: 0.4,
                      order: 0,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  scales: { y: { beginAtZero: true } },
                  plugins: {
                    legend: { display: true },
                    title: {
                      display: true,
                      position: 'top',
                      text: 'Distribuzione spese tracciate per mese',
                      color: '#000',
                    },
                  },
                },
              }
            );
            // LINES
            // CATEGORIES
            const categories = this._purchasesTracesService.categories;
            const categoriesLabels = categories.map((c) => c.label);
            const datasets: any = [];
            monthsLabels.forEach((month) => {
              response.data?.forEach(({ year, months }) => {
                const valuesForCategories = categoriesLabels.map(
                  (categoryLabel) => {
                    const monthData = months?.find(
                      (m) => m.name?.toLowerCase() === month?.toLowerCase()
                    );
                    const categoryData = monthData?.categories?.find(
                      (c) => c.category === categoryLabel
                    );
                    return categoryData?.totalAmount ?? 0;
                  }
                );
                const backgroundColors = categoriesLabels.map((categoryLabel) =>
                  this._purchasesTracesService.getCategoryColor(categoryLabel)
                );
                datasets.push({
                  label: `${month} ${year}`,
                  data: valuesForCategories,
                  backgroundColor: backgroundColors,
                  borderWidth: 0.3,
                });
              });
            });
            const graficoLineeDivisioneCategorieDatasets = (
              response.data || []
            ).map((dataset) => {
              let backgroundColor: string[] = [];
              const data = this._purchasesTracesService.categories.map(
                (cat) => {
                  backgroundColor.push(cat.color);
                  const categoryData = dataset.categories?.filter(
                    (catStat) => catStat.category === cat.label
                  );
                  return categoryData?.at(0)?.totalAmount || 0;
                }
              );

              return {
                label: `${dataset.year}`,
                data,
                backgroundColor,
                borderWidth: 0.3,
              };
            });
            this.graficoLineeDivisioneCategorie = new Chart(
              'graficoLineeDivisioneCategorie',
              {
                type: 'bar',
                data: {
                  labels: categoriesLabels,
                  datasets: graficoLineeDivisioneCategorieDatasets,
                },
                options: {
                  responsive: true,
                  plugins: {
                    title: {
                      display: true,
                      position: 'top',
                      color: '#000',
                      padding: { bottom: 16 },
                      text: [
                        `Distribuzione spese divise per categoria`,
                        `(${previousYearData?.year || '-'}/${
                          currentYearData?.year || '-'
                        })`,
                      ],
                    },
                    legend: { position: 'top' },
                    tooltip: {
                      mode: 'point',
                      callbacks: {
                        label: (tooltipItem) => {
                          return ` Anno ${
                            tooltipItem.datasetIndex == 0
                              ? currentYearData?.year
                              : previousYearData?.year
                          }: ${tooltipItem.raw} €`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Categorie',
                      },
                    },
                    y: {
                      title: {
                        display: true,
                        text: 'Totale speso (€)',
                      },
                    },
                  },
                },
              }
            );
            // CATEGORIES
          }, 450);
        },
        error: (err) => {
          console.error('getPurchasesTracesStatistics Error:', err);
        },
      });
  }

  protected goBackOnePage() {
    this._baseComponentService.backOnePage();
  }
}
